import React from 'react'
import { cn } from '../../utils'

/**
 * Layout components for adaptive design system
 * Provides consistent layout patterns across different screen sizes
 */

// Main container component
const Container = React.forwardRef(({
  className,
  size = 'default',
  adaptive = true,
  children,
  ...props
}, ref) => {
  const sizeVariants = {
    sm: 'max-w-screen-sm',
    default: 'max-w-screen-lg',
    lg: 'max-w-screen-xl',
    xl: 'max-w-screen-2xl',
    full: 'max-w-full'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'w-full mx-auto',
        sizeVariants[size],
        adaptive && [
          'px-2 sm:px-4 lg:px-6',
          'container-adaptive'
        ],
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Container.displayName = 'Container'

// Grid layout component
const Grid = React.forwardRef(({
  className,
  cols = 'auto',
  gap = 'default',
  adaptive = true,
  children,
  ...props
}, ref) => {
  const colVariants = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    auto: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  }
  
  const gapVariants = {
    sm: 'gap-2 lg:gap-3',
    default: 'gap-4 lg:gap-6',
    lg: 'gap-6 lg:gap-8',
    xl: 'gap-8 lg:gap-10'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'grid',
        colVariants[cols] || 'grid-adaptive-auto',
        gapVariants[gap],
        adaptive && 'space-adaptive-md',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Grid.displayName = 'Grid'

// Flex layout component
const Flex = React.forwardRef(({
  className,
  direction = 'row',
  align = 'start',
  justify = 'start',
  wrap = false,
  gap = 'default',
  adaptive = true,
  children,
  ...props
}, ref) => {
  const directionVariants = {
    row: 'flex-row',
    col: 'flex-col',
    'row-reverse': 'flex-row-reverse',
    'col-reverse': 'flex-col-reverse',
    adaptive: 'flex-col md:flex-row'
  }
  
  const alignVariants = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
    baseline: 'items-baseline'
  }
  
  const justifyVariants = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly'
  }
  
  const gapVariants = {
    sm: 'gap-2',
    default: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'flex',
        directionVariants[direction],
        alignVariants[align],
        justifyVariants[justify],
        wrap && 'flex-wrap',
        gapVariants[gap],
        adaptive && 'space-adaptive-md',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Flex.displayName = 'Flex'

// Sidebar layout component
const SidebarLayout = React.forwardRef(({
  className,
  sidebarWidth = 'default',
  collapsible = false,
  collapsed = false,
  children,
  ...props
}, ref) => {
  const widthVariants = {
    sm: collapsed ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[200px_1fr]',
    default: collapsed ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[240px_1fr]',
    lg: collapsed ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[280px_1fr]',
    xl: collapsed ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[320px_1fr]'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-1',
        widthVariants[sidebarWidth],
        'min-h-screen',
        'transition-all duration-300 ease-in-out',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

SidebarLayout.displayName = 'SidebarLayout'

// Three-panel layout for desktop
const ThreePanelLayout = React.forwardRef(({
  className,
  leftWidth = '300px',
  rightWidth = '300px',
  gap = 'default',
  children,
  ...props
}, ref) => {
  const gapVariants = {
    sm: 'lg:gap-4',
    default: 'lg:gap-6',
    lg: 'lg:gap-8'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-1',
        `lg:grid-cols-[${leftWidth}_1fr_${rightWidth}]`,
        gapVariants[gap],
        'min-h-screen',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

ThreePanelLayout.displayName = 'ThreePanelLayout'

// Stack component for vertical layouts
const Stack = React.forwardRef(({
  className,
  spacing = 'default',
  adaptive = true,
  children,
  ...props
}, ref) => {
  const spacingVariants = {
    sm: 'space-y-2',
    default: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col',
        spacingVariants[spacing],
        adaptive && 'space-adaptive-md',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Stack.displayName = 'Stack'

// Section component for content areas
const Section = React.forwardRef(({
  className,
  padding = 'default',
  adaptive = true,
  children,
  ...props
}, ref) => {
  const paddingVariants = {
    none: '',
    sm: 'py-8',
    default: 'py-12 lg:py-16',
    lg: 'py-16 lg:py-20',
    xl: 'py-20 lg:py-24'
  }
  
  return (
    <section
      ref={ref}
      className={cn(
        paddingVariants[padding],
        adaptive && 'padding-adaptive',
        className
      )}
      {...props}
    >
      {children}
    </section>
  )
})

Section.displayName = 'Section'

export {
  Container,
  Flex,
  SidebarLayout,
  ThreePanelLayout,
  Stack,
  Section
}